// src/components/AskMeAnything.js
// All members can ask Sky Bri questions
// Gold: 1/week, Premium+Platinum: 2/week
// Answers appear in this tab with notification
import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import './AskMeAnything.css'

function getWeekNumber() {
  const now  = new Date()
  const start = new Date(now.getFullYear(), 0, 1)
  return Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7)
}

export default function AskMeAnything({ tier, onUnread }) {
  const { session } = useAuth()
  const userId = session?.user?.id
  const weekNum  = getWeekNumber()
  const weekYear = new Date().getFullYear()

  const maxQuestions = tier === 'gold' ? 1 : 2

  const [questions, setQuestions] = useState([])
  const [input, setInput]         = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [weekCount, setWeekCount]   = useState(0)
  const [newAnswers, setNewAnswers]  = useState(0)

  useEffect(() => {
    if (!userId) return
    loadQuestions()

    // Real-time subscription for answers
    const sub = supabase.channel('ama-' + userId)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'ama_questions',
        filter: `user_id=eq.${userId}`
      }, payload => {
        if (payload.new.answered && !payload.old.answered) {
          setQuestions(prev => prev.map(q =>
            q.id === payload.new.id ? payload.new : q
          ))
          setNewAnswers(c => {
            const n = c + 1
            if (onUnread) onUnread(n)
            return n
          })
        }
      })
      .subscribe()

    return () => sub.unsubscribe()
  }, [userId]) // eslint-disable-line

  async function loadQuestions() {
    const { data } = await supabase
      .from('ama_questions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (data) {
      setQuestions(data)
      const thisWeek = data.filter(
        q => q.week_number === weekNum && q.week_year === weekYear
      ).length
      setWeekCount(thisWeek)

      // Count unread answers
      const unread = data.filter(q => q.answered && !q.read_at).length
      if (unread > 0 && onUnread) onUnread(unread)
      setNewAnswers(unread)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!input.trim() || submitting) return
    if (weekCount >= maxQuestions) return

    setSubmitting(true)
    const { error } = await supabase
      .from('ama_questions')
      .insert({
        user_id:     userId,
        question:    input.trim(),
        week_number: weekNum,
        week_year:   weekYear,
        answered:    false
      })
    setSubmitting(false)
    if (error) { alert('Failed to submit. Try again.'); return }
    setInput('')
    setWeekCount(c => c + 1)
    loadQuestions()
  }

  const remaining = maxQuestions - weekCount
  const canAsk    = remaining > 0

  return (
    <div className="ama">
      <div className="ama__header">
        <div>
          <p className="ama__eyebrow">Ask Me Anything</p>
          <h2 className="ama__title">Your Direct Line to Sky Bri</h2>
          <p className="ama__sub">
            Ask anything. Sky Bri reads every question and replies personally.
          </p>
        </div>
        <div className="ama__quota">
          <span className="ama__quota-num" style={{ color: canAsk ? 'var(--gold)' : 'var(--dim)' }}>
            {remaining}
          </span>
          <span className="ama__quota-label">
            question{remaining !== 1 ? 's' : ''} left this week
          </span>
        </div>
      </div>

      {/* New answers notification */}
      {newAnswers > 0 && (
        <div className="ama__new-answers" onClick={() => setNewAnswers(0)}>
          🌹 Sky Bri answered {newAnswers} of your question{newAnswers > 1 ? 's' : ''}! Scroll down to read.
        </div>
      )}

      {/* Ask form */}
      {canAsk ? (
        <form onSubmit={handleSubmit} className="ama__form">
          <textarea
            className="ama__input"
            placeholder="What would you like to ask Sky Bri?"
            value={input}
            onChange={e => setInput(e.target.value)}
            rows={3}
            maxLength={500}
          />
          <div className="ama__form-footer">
            <span className="ama__char">{input.length}/500</span>
            <button className="btn btn--gold" type="submit" disabled={submitting || !input.trim()}>
              {submitting ? 'Sending...' : 'Ask Sky Bri'}
            </button>
          </div>
        </form>
      ) : (
        <div className="ama__limit">
          <p>You've used all {maxQuestions} question{maxQuestions > 1 ? 's' : ''} for this week.</p>
          <p className="ama__limit-sub">
            {tier === 'gold'
              ? 'Upgrade to Premium for 2 questions per week.'
              : 'New questions unlock every Monday.'}
          </p>
        </div>
      )}

      {/* Questions list */}
      <div className="ama__list">
        {questions.length === 0 && (
          <div className="ama__empty">
            No questions yet. Ask your first one above.
          </div>
        )}

        {questions.map(q => (
          <div key={q.id} className={`ama-item ${q.answered ? 'ama-item--answered' : 'ama-item--pending'}`}>
            <div className="ama-item__question">
              <span className="ama-item__q-label">Your question</span>
              <p className="ama-item__q-text">{q.question}</p>
              <span className="ama-item__date">
                {new Date(q.created_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric'
                })}
              </span>
            </div>

            {q.answered ? (
              <div className="ama-item__answer">
                <div className="ama-item__answer-avatar">
                  S <span className="chat-verified" style={{position:'relative',bottom:'auto',right:'auto',display:'inline'}}>✦</span>
                </div>
                <div>
                  <p className="ama-item__answer-label">Sky Bri replied</p>
                  <p className="ama-item__answer-text">{q.answer}</p>
                </div>
              </div>
            ) : (
              <div className="ama-item__pending">
                <div className="ama-item__pending-dot" />
                Sky Bri will reply soon
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
